import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';

// API 기본 설정 - backend-api-v2 사용
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8091/api/v1',
});

// 인터셉터 설정 - 모든 요청에 토큰 추가
api.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem('auth-storage'))?.state?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 401 응답 시 자동 로그아웃
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 토큰이 만료되었거나 유효하지 않음
      const { logout } = useAuthStore.getState();
      logout();
    }
    return Promise.reject(error);
  }
);

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      permissions: [],
      roles: [],
      isAuthenticated: false,
      isLoading: false,

      // 로그인
      login: async (credentials) => {
        set({ isLoading: true });
        try {
          // 새 API 엔드포인트 사용
          const response = await api.post('/auth/login', {
            username: credentials.id || credentials.username,
            password: credentials.password,
          });

          const { access_token, refresh_token, expires_in } = response.data;

          // 토큰 저장
          set({
            token: access_token,
            refreshToken: refresh_token,
          });

          // 사용자 정보 조회
          const userResponse = await api.get('/auth/me', {
            headers: { Authorization: `Bearer ${access_token}` },
          });

          const { id, username, email, roles, permissions } = userResponse.data;

          set({
            user: { id, username, email },
            roles,
            permissions,
            isAuthenticated: true,
            isLoading: false,
          });

          return { success: true };
        } catch (error) {
          set({ isLoading: false });
          const errorMessage = error.response?.data?.detail || '로그인 실패';
          return { success: false, error: errorMessage };
        }
      },

      // 로그아웃
      logout: async () => {
        const { token } = get();
        if (token) {
          try {
            await api.post('/auth/logout', {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch (e) {
            // 로그아웃 실패해도 로컬 상태는 초기화
          }
        }

        set({
          user: null,
          token: null,
          refreshToken: null,
          permissions: [],
          roles: [],
          isAuthenticated: false,
        });
      },

      // 권한 체크
      hasPermission: (permission) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

      // 역할 체크
      hasRole: (role) => {
        const { roles } = get();
        return roles.includes(role);
      },

      // 토큰 갱신
      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        try {
          const response = await api.post('/auth/refresh', {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;
          set({
            token: access_token,
            refreshToken: refresh_token,
          });
          return true;
        } catch (e) {
          get().logout();
          return false;
        }
      },

      // 사용자 정보 복원
      restoreUser: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await api.get('/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
          });

          const { id, username, email, roles, permissions } = response.data;
          set({
            user: { id, username, email },
            roles,
            permissions,
            isAuthenticated: true,
          });
        } catch (e) {
          // 토큰이 유효하지 않으면 로그아웃
          get().logout();
        }
      },

      // API 호출을 위한 axios 인스턴스 제공
      getApi: () => api,
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        roles: state.roles,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
